import { TowerType, Tower } from '../entities/Tower';
import { ProgressState } from '../state/ProgressState';
import { SessionManager } from './SessionManager';

// ============================================
// Tower Unlock System
// Based on PixelJunk Monsters balancing guide
// ============================================

// Tower availability tiers:
// - Starter (Tier 1): Arrow, Cannon, Anti-Air - Always available, free to use
// - Research-Gated (Tier 2): Tesla, Laser, Mortar - Available but costs gems to research for the level
// - Special Unlock (Tier 3): Ice, Fire, Hive, Lightning, Trap, Gem - Must unlock via progression, then research

export interface TowerUnlockRequirement {
  type: 'starter' | 'research' | 'special';
  unlockScenario?: string;  // For special unlock towers
  description: string;
}

interface TowerUnlockInfo {
  tower: TowerType;
  tier: number;
  unlocked: boolean;          // Has been unlocked via progression
  researched: boolean;        // Has been researched this session (costs gems)
  researchCost: number;       // Gems to research
  requirement?: TowerUnlockRequirement;
}

// Tier 1 - Starter (always available, no research needed)
// Tier 2 - Research-Gated (available, but need to spend gems to research each level)
// Tier 3 - Special Unlock (unlock via level completion, then research)
const TOWER_UNLOCK_REQUIREMENTS: Record<TowerType, TowerUnlockRequirement> = {
  // Tier 1 - Starter (free) - Available from F1
  arrow: { type: 'starter', description: 'Starter tower' },
  cannon: { type: 'starter', description: 'Starter tower' },
  antiair: { type: 'starter', description: 'Starter tower' },

  // Tier 2 - Research-Gated (available but costs gems to research)
  // Tesla unlocked at F3, Laser at S1, Mortar at S2
  tesla: { type: 'research', description: 'Research: 10 gems' },
  laser: { type: 'research', description: 'Research: 11 gems' },
  mortar: { type: 'research', description: 'Research: 15 gems' },

  // Tier 3 - Special Unlock (unlock via scenario completion, then research)
  // Ice unlocked at S3, Fire at R1, Hive at R2, Trap at R3
  // Lightning at V1, Gem at V1
  ice: { type: 'special', unlockScenario: 'S3', description: 'Complete Bog Maze (S3)' },
  fire: { type: 'special', unlockScenario: 'R1', description: 'Complete Broken Roads (R1)' },
  hive: { type: 'special', unlockScenario: 'R2', description: 'Complete Twin Roads (R2)' },
  trap: { type: 'special', unlockScenario: 'R3', description: 'Complete Spiral Ridge (R3)' },
  lightning: { type: 'special', unlockScenario: 'V1', description: 'Complete Lava Flow (V1)' },
  gem: { type: 'special', unlockScenario: 'V1', description: 'Complete Lava Flow (V1)' },
};

class TowerUnlockSystemManager {
  // Permanently unlocked towers (via progression)
  private unlockedTowers: Set<TowerType> = new Set();
  // Researched towers for current session (resets each level)
  private researchedTowers: Set<TowerType> = new Set();
  // Allowed towers for current scenario (from scenario definition)
  private scenarioAllowedTowers: Set<TowerType> = new Set();

  constructor() {
    this.loadUnlocks();
  }

  /**
   * Set the allowed towers for the current scenario
   */
  setScenarioTowers(allowedTowers: TowerType[]): void {
    this.scenarioAllowedTowers.clear();
    allowedTowers.forEach(t => this.scenarioAllowedTowers.add(t));
    console.log(`[TowerUnlockSystem] Scenario allows: ${allowedTowers.join(', ')}`);
  }

  /**
   * Pre-research towers for custom levels (start already researched)
   */
  setPreResearchedTowers(towers: TowerType[]): void {
    towers.forEach(t => {
      this.researchedTowers.add(t);
      // Also ensure they're marked as unlocked for this session
      this.unlockedTowers.add(t);
    });
    console.log(`[TowerUnlockSystem] Pre-researched: ${towers.join(', ')}`);
  }

  /**
   * Load unlocks from progress state
   */
  loadUnlocks(): void {
    // Tier 1 starters are always unlocked
    this.unlockedTowers.add('arrow');
    this.unlockedTowers.add('cannon');
    this.unlockedTowers.add('antiair');

    // Tier 2 research-gated are always unlocked (but need gems to research)
    this.unlockedTowers.add('tesla');
    this.unlockedTowers.add('laser');
    this.unlockedTowers.add('mortar');

    // Check special unlocks based on progress
    this.checkUnlocks();
  }

  /**
   * Check and update unlocks based on current progress
   */
  checkUnlocks(): void {
    for (const [tower, requirement] of Object.entries(TOWER_UNLOCK_REQUIREMENTS)) {
      const towerType = tower as TowerType;
      if (this.unlockedTowers.has(towerType)) continue;

      if (requirement.type === 'special' && requirement.unlockScenario) {
        if (ProgressState.isScenarioCompleted(requirement.unlockScenario)) {
          this.unlock(towerType);
        }
      }
    }
  }

  /**
   * Permanently unlock a tower type (via progression)
   */
  unlock(towerType: TowerType): void {
    if (this.unlockedTowers.has(towerType)) return;

    this.unlockedTowers.add(towerType);
    console.log(`[TowerUnlockSystem] Unlocked: ${towerType}`);

    // Save to progress state
    ProgressState.unlockTower?.(towerType);
  }

  /**
   * Research a tower for this session (costs gems)
   * Returns true if successful, false if can't afford
   */
  researchTower(towerType: TowerType): boolean {
    if (this.researchedTowers.has(towerType)) return true;
    if (!this.unlockedTowers.has(towerType)) return false;

    const stats = Tower.getStats(towerType);
    const cost = stats.researchCost;

    // Starters don't need research
    if (cost === 0) {
      this.researchedTowers.add(towerType);
      return true;
    }

    // Check if can afford
    if (!SessionManager.canAffordGems(cost)) {
      console.log(`[TowerUnlockSystem] Can't afford research for ${towerType} (need ${cost} gems)`);
      return false;
    }

    // Spend gems and research
    SessionManager.spendGems(cost, 'P1');
    this.researchedTowers.add(towerType);
    console.log(`[TowerUnlockSystem] Researched: ${towerType} for ${cost} gems`);
    return true;
  }

  /**
   * Check if a tower is unlocked (via progression)
   */
  isUnlocked(towerType: TowerType): boolean {
    return this.unlockedTowers.has(towerType);
  }

  /**
   * Check if a tower is researched (ready to build this session)
   */
  isResearched(towerType: TowerType): boolean {
    return this.researchedTowers.has(towerType);
  }

  /**
   * Check if a tower is allowed in the current scenario
   */
  isAllowedInScenario(towerType: TowerType): boolean {
    // If no scenario towers set, all are allowed
    if (this.scenarioAllowedTowers.size === 0) return true;
    return this.scenarioAllowedTowers.has(towerType);
  }

  /**
   * Check if a tower can be built (allowed in scenario + unlocked + researched)
   */
  canBuild(towerType: TowerType): boolean {
    // Must be allowed in this scenario
    if (!this.isAllowedInScenario(towerType)) return false;
    
    // Must be unlocked via progression
    if (!this.isUnlocked(towerType)) return false;
    
    const stats = Tower.getStats(towerType);
    // Starters (cost 0) don't need research
    if (stats.researchCost === 0) return true;
    
    return this.isResearched(towerType);
  }

  /**
   * Get research cost for a tower
   */
  getResearchCost(towerType: TowerType): number {
    return Tower.getStats(towerType).researchCost;
  }

  /**
   * Get all unlocked towers (may or may not be researched)
   */
  getUnlockedTowers(): TowerType[] {
    return Array.from(this.unlockedTowers);
  }

  /**
   * Get available towers for building (unlocked + researched or free)
   */
  getAvailableTowers(): TowerType[] {
    return this.getUnlockedTowers().filter(t => this.canBuild(t));
  }

  /**
   * Get towers to display in UI (allowed in scenario + unlocked, even if not researched)
   * These are the towers that COULD be used if researched
   */
  getDisplayableTowers(): TowerType[] {
    return this.getUnlockedTowers().filter(t => this.isAllowedInScenario(t));
  }

  /**
   * Check if a tower needs research before it can be used
   */
  needsResearch(towerType: TowerType): boolean {
    if (!this.isUnlocked(towerType)) return false;
    const stats = Tower.getStats(towerType);
    if (stats.researchCost === 0) return false;
    return !this.isResearched(towerType);
  }

  /**
   * Get all towers info for UI display
   */
  getAllTowersInfo(): TowerUnlockInfo[] {
    const allTypes = Tower.getAllTypes();
    return allTypes.map(tower => {
      const stats = Tower.getStats(tower);
      return {
        tower,
        tier: stats.tier,
        unlocked: this.isUnlocked(tower),
        researched: this.isResearched(tower) || stats.researchCost === 0,
        researchCost: stats.researchCost,
        requirement: TOWER_UNLOCK_REQUIREMENTS[tower],
      };
    });
  }

  /**
   * Get unlock requirement for a tower
   */
  getRequirement(towerType: TowerType): TowerUnlockRequirement {
    return TOWER_UNLOCK_REQUIREMENTS[towerType];
  }

  /**
   * Get status text for a tower
   */
  getStatusText(towerType: TowerType): string {
    if (!this.isUnlocked(towerType)) {
      const req = TOWER_UNLOCK_REQUIREMENTS[towerType];
      return req.description;
    }

    const stats = Tower.getStats(towerType);
    if (stats.researchCost === 0) {
      return 'Ready';
    }

    if (this.isResearched(towerType)) {
      return 'Researched';
    }

    return `Research: ${stats.researchCost} gems`;
  }

  /**
   * Reset research for new level (keeps permanent unlocks)
   */
  resetResearch(): void {
    this.researchedTowers.clear();
    this.scenarioAllowedTowers.clear();
    // Auto-research starter towers
    this.researchedTowers.add('arrow');
    this.researchedTowers.add('cannon');
    this.researchedTowers.add('antiair');
    console.log('[TowerUnlockSystem] Research reset for new level');
  }

  /**
   * Debug: unlock and research all towers
   */
  debugUnlockAll(): void {
    const allTypes = Tower.getAllTypes();
    allTypes.forEach(type => {
      this.unlockedTowers.add(type);
      this.researchedTowers.add(type);
    });
    console.log('[TowerUnlockSystem] All towers unlocked and researched (debug)');
  }

  /**
   * Reset all (for new game)
   */
  reset(): void {
    this.unlockedTowers.clear();
    this.researchedTowers.clear();
    this.loadUnlocks();
    this.resetResearch();
  }
}

// Singleton instance
export const TowerUnlockSystem = new TowerUnlockSystemManager();
